import { useSendNotification } from "@app/hooks/useNotification";
import type {
  GetPokeNoWorkspaceAuthContextResponseType,
  GetPokeWorkspaceAuthContextResponseType,
} from "@app/lib/api/poke/auth_context";
import type { GetPokeMetronomePackagesResponseBody } from "@app/lib/api/poke/metronome";
import { useRegionContext } from "@app/lib/auth/RegionContext";
import { clientFetch } from "@app/lib/egress/client";
import { emptyArray, useFetcher, useSWRWithDefaults } from "@app/lib/swr/swr";
import { isRegionRedirect } from "@app/lib/swr/workspaces";
import type { GetDataSourcePermissionsResponseBody } from "@app/types/api/data_sources/managed_permissions";
import type {
  GetPokeCouponRedemptionsResponseBody,
  GetPokeCouponsResponseBody,
} from "@app/types/api/poke/coupons";
import type { GetPokeFeaturesResponseBody } from "@app/types/api/poke/features";
import type { GetPokePlansResponseBody } from "@app/types/api/poke/plans";
import type { PostPokeStripeCustomerCurrencyResponseBody } from "@app/types/api/poke/stripe_customers";
import type { GetRegionResponseType } from "@app/types/api/regions/config";
import type { ConnectorPermission } from "@app/types/connectors/connectors_api";
import type { DataSourceType } from "@app/types/data_source";
import type { APIErrorResponse, RegionRedirectError } from "@app/types/error";
import { isAPIErrorResponse } from "@app/types/error";
import type { LightWorkspaceType } from "@app/types/user";
import { useEffect } from "react";
import type { Fetcher } from "swr";
import { useSWRConfig } from "swr";

export function usePokeRegion() {
  const { fetcher } = useFetcher();
  const regionFetcher: Fetcher<GetRegionResponseType> = fetcher;

  const { data, error } = useSWRWithDefaults("/api/poke/region", regionFetcher);

  return {
    regionData: data,
    isRegionLoading: !error && !data,
    isRegionError: error,
  };
}

export function usePokeConnectorPermissions({
  owner,
  dataSource,
  parentId,
  filterPermission,
  disabled,
}: {
  owner: LightWorkspaceType;
  dataSource: DataSourceType;
  parentId: string | null;
  filterPermission: ConnectorPermission | null;
  disabled?: boolean;
}) {
  const { fetcher } = useFetcher();
  const permissionsFetcher: Fetcher<GetDataSourcePermissionsResponseBody> =
    fetcher;

  let url = `/api/poke/workspaces/${owner.sId}/data_sources/${dataSource.sId}/managed/permissions?viewType=document`;
  if (parentId) {
    url += `&parentId=${parentId}`;
  }
  if (filterPermission) {
    url += `&filterPermission=${filterPermission}`;
  }

  const { data, error } = useSWRWithDefaults(url, permissionsFetcher, {
    disabled,
  });

  return {
    resources: data?.resources ?? emptyArray(),
    isResourcesLoading: !error && !data,
    isResourcesError: error,
    resourcesError: isAPIErrorResponse(error) ? error.error : null,
  };
}

export function usePokePlans() {
  const { fetcher } = useFetcher();
  const plansFetcher: Fetcher<GetPokePlansResponseBody> = fetcher;

  const { data, error } = useSWRWithDefaults("/api/poke/plans", plansFetcher);

  return {
    plans: data?.plans ?? emptyArray(),
    isPlansLoading: !error && !data,
    isPlansError: error,
  };
}

export function usePokeCoupons() {
  const { fetcher } = useFetcher();
  const couponsFetcher: Fetcher<GetPokeCouponsResponseBody> = fetcher;

  const { data, error, mutate } = useSWRWithDefaults(
    "/api/poke/coupons",
    couponsFetcher
  );

  return {
    coupons: data?.coupons ?? emptyArray(),
    canCreateCoupon: data?.canCreateCoupon ?? false,
    isCouponsLoading: !error && !data,
    isCouponsError: error,
    mutate,
  };
}

export function usePokeCouponRedemptions({
  couponId,
  disabled,
}: {
  couponId: string;
  disabled: boolean;
}) {
  const { fetcher } = useFetcher();
  const redemptionsFetcher: Fetcher<GetPokeCouponRedemptionsResponseBody> =
    fetcher;

  const { data, error } = useSWRWithDefaults(
    `/api/poke/coupons/${couponId}/redemptions`,
    redemptionsFetcher,
    { disabled }
  );

  return {
    redemptions: data?.redemptions ?? emptyArray(),
    isRedemptionsLoading: !error && !data && !disabled,
    isRedemptionsError: error,
  };
}

export function usePokeArchiveCoupon() {
  const { mutate } = useSWRConfig();
  const sendNotification = useSendNotification();

  return async (couponId: string) => {
    const r = await clientFetch(`/api/poke/coupons/${couponId}/archive`, {
      method: "POST",
    });
    if (!r.ok) {
      sendNotification({
        title: "Error archiving coupon",
        type: "error",
        description: `Something went wrong: ${r.status} ${await r.text()}`,
      });
      return;
    }
    await mutate("/api/poke/coupons");
    sendNotification({ title: "Coupon archived", type: "success" });
  };
}

export function usePokeCancelPendingContract() {
  const sendNotification = useSendNotification();

  return async (owner: LightWorkspaceType): Promise<boolean> => {
    const r = await clientFetch(
      `/api/poke/workspaces/${owner.sId}/cancel_pending_contract`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({}),
      }
    );
    if (!r.ok) {
      sendNotification({
        title: "Error cancelling pending subscription",
        type: "error",
        description: `Something went wrong: ${r.status} ${await r.text()}`,
      });
      return false;
    }
    sendNotification({
      title: "Pending subscription cancelled",
      type: "success",
    });
    return true;
  };
}

export function usePokeMetronomePackages({
  disabled,
}: {
  disabled?: boolean;
} = {}) {
  const { fetcher } = useFetcher();
  const packagesFetcher: Fetcher<GetPokeMetronomePackagesResponseBody> =
    fetcher;

  const { data, error } = useSWRWithDefaults(
    "/api/poke/metronome/packages",
    packagesFetcher,
    { disabled }
  );

  return {
    packages: data?.packages ?? emptyArray(),
    isPackagesLoading: !error && !data && !disabled,
    isPackagesError: error,
    packagesError: isAPIErrorResponse(error) ? error.error : null,
  };
}

export function usePokeStripeCustomerCurrency({
  stripeCustomerId,
  disabled,
}: {
  stripeCustomerId: string | null;
  disabled?: boolean;
}) {
  const { fetcherWithBody } = useFetcher();

  const enabled = !disabled && !!stripeCustomerId;
  const { data, error } = useSWRWithDefaults<
    string | null,
    PostPokeStripeCustomerCurrencyResponseBody
  >(
    enabled
      ? JSON.stringify({
          url: "/api/poke/stripe/customers/currency",
          stripeCustomerId,
        })
      : null,
    async () => {
      if (!stripeCustomerId) {
        throw new Error("stripeCustomerId is required.");
      }

      return fetcherWithBody([
        "/api/poke/stripe/customers/currency",
        { stripeCustomerId },
        "POST",
      ]);
    },
    { disabled: !enabled }
  );

  return {
    currency: data?.currency ?? null,
    isCurrencyLoading: enabled && !error && !data,
    currencyError: isAPIErrorResponse(error) ? error.error : null,
  };
}

export function usePokeFeatureFlags({
  disabled,
  owner,
}: {
  disabled?: boolean;
  owner: LightWorkspaceType;
}) {
  const { fetcher } = useFetcher();
  const featureFlagsFetcher: Fetcher<GetPokeFeaturesResponseBody> = fetcher;

  const { data, error, mutate } = useSWRWithDefaults(
    `/api/poke/workspaces/${owner.sId}/features`,
    featureFlagsFetcher,
    { disabled }
  );

  return {
    data: data?.features ?? emptyArray(),
    isLoading: !error && !data,
    isError: error,
    mutate,
  };
}

export function usePokeWorkOSDSyncStatus({
  disabled,
  owner,
}: {
  disabled?: boolean;
  owner: LightWorkspaceType;
}) {
  const { fetcher } = useFetcher();
  const { data, error, mutate } = useSWRWithDefaults(
    `/api/poke/workspaces/${owner.sId}/dsync`,
    fetcher,
    { disabled }
  );

  return {
    dsyncStatus: data,
    error,
    isLoading: !error && !data,
    mutate,
  };
}

interface UsePokeAuthContextResult<T> {
  authContext: T | undefined;
  isAuthenticated: boolean;
  isAuthContextLoading: boolean;
  authContextError: APIErrorResponse | Error | undefined;
}

export function usePokeAuthContext(options?: {
  disabled?: boolean;
}): UsePokeAuthContextResult<GetPokeNoWorkspaceAuthContextResponseType>;

export function usePokeAuthContext(options: {
  workspaceId: string;
  disabled?: boolean;
}): UsePokeAuthContextResult<
  Exclude<GetPokeWorkspaceAuthContextResponseType, RegionRedirectError>
>;

export function usePokeAuthContext(
  options: { workspaceId?: string; disabled?: boolean } = {}
) {
  const { fetcher } = useFetcher();
  const { workspaceId, disabled } = options;
  const regionContext = useRegionContext();

  const url = workspaceId
    ? `/api/poke/workspaces/${workspaceId}/auth-context`
    : `/api/poke/auth-context`;

  const { data, isLoading, error, mutate } = useSWRWithDefaults<
    string,
    | GetPokeNoWorkspaceAuthContextResponseType
    | GetPokeWorkspaceAuthContextResponseType
  >(url, fetcher, { disabled });

  const isRegionRedirectResponse = error && isRegionRedirect(error.error);
  const regionRedirect = isRegionRedirectResponse
    ? error.error.redirect
    : undefined;
  const isAuthenticated =
    !isRegionRedirectResponse && !!data?.user && data.isSuperUser;

  // Handle region redirect.
  useEffect(() => {
    if (regionRedirect) {
      regionContext.setRegionInfo({
        name: regionRedirect.region,
        url: regionRedirect.url,
      });
      void mutate();
    }
  }, [regionRedirect, mutate, regionContext]);

  return {
    authContext: isRegionRedirectResponse ? undefined : data,
    isAuthenticated,
    isAuthContextLoading: isLoading || !!isRegionRedirectResponse,
    authContextError: error,
  };
}
